/**
 * AdminEventBilling — corporate / manual invoice creator.
 *
 * Admin captures client details (B2B GSTIN supported), adds any number of
 * facility slots + free-form service lines with a flat negotiated price,
 * and generates one GST invoice. Facility slots block the Slot Matrix so
 * they can't be double-booked.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, X, Trash2, Save, CalendarClock, Package, FileText, Building2, Ban, IndianRupee, Download } from "lucide-react";

const HOURS = Array.from({ length: 18 }, (_, i) => 6 + i); // 6 → 23

export default function AdminEventBilling() {
  const [bills, setBills] = useState([]);
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);

  const load = () => {
    api.get("/admin/event-bills").then((r) => setBills(r.data)).catch(() => {});
    api.get("/admin/service-configs").then((r) => setServices(r.data.filter((s) => s.type === "hourly"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Billing · Event & Corporate</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Event Billing.</h1>
          <p className="text-sm text-white/60 mt-3 max-w-3xl">
            Fix/book any facility on any date at a negotiated price, add any number of add-on services, capture the client&apos;s GSTIN for B2B invoicing — one GST invoice per event, slots auto-blocked on the matrix.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="event-billing-new-btn"
          className="bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-widest text-xs px-4 py-3 inline-flex items-center gap-2"
        >
          <Plus size={14}/> New Event Bill
        </button>
      </div>

      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5">
            <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
              <th className="p-3">Invoice #</th>
              <th className="p-3">Event</th>
              <th className="p-3">Client (GSTIN)</th>
              <th className="p-3">Event Date</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3 text-right">Balance</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {bills.length === 0 && (
              <tr><td colSpan={8} className="text-center p-10 text-white/40">No event bills yet. Tap &quot;New Event Bill&quot; to create one.</td></tr>
            )}
            {bills.map((b) => (
              <tr key={b.id} className="border-t border-white/8">
                <td className="p-3 font-mono">{b.invoice_number || "—"}</td>
                <td className="p-3 font-semibold">{b.event_title}</td>
                <td className="p-3">
                  <div>{b.client_name}</div>
                  {b.client_gstin && <div className="text-[10px] font-mono text-white/50">{b.client_gstin}</div>}
                </td>
                <td className="p-3 text-white/70">{b.event_date || "—"}</td>
                <td className="p-3 text-right font-mono">{formatINR(b.total)}</td>
                <td className="p-3 text-right font-mono">{formatINR(b.balance_due)}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                    b.status === "paid" ? "bg-green-500/20 text-green-300"
                      : b.status === "partial" ? "bg-yellow-500/20 text-yellow-300"
                      : b.status === "cancelled" ? "bg-red-500/20 text-red-300"
                      : "bg-white/10 text-white/70"}`}>{b.status}</span>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => setShowDetail(b)}
                    className="text-brand-blue hover:underline text-[10px] font-mono uppercase">Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <NewBillModal services={services} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {showDetail && <DetailModal bill={showDetail} onClose={() => setShowDetail(null)} onChanged={() => { load(); setShowDetail(null); }} />}
    </div>
  );
}

function NewBillModal({ services, onClose, onSaved }) {
  const [client, setClient] = useState({ client_name: "", client_phone: "", client_email: "", client_gstin: "", client_address: "", event_title: "", event_date: "", notes: "" });
  const [facilities, setFacilities] = useState([]);
  const [addons, setAddons] = useState([]);
  const [busy, setBusy] = useState(false);

  const addFacility = () => {
    const svc = services[0];
    if (!svc) { toast.error("No hourly services available"); return; }
    setFacilities((rows) => [...rows, {
      service_slug: svc.slug, service_name: svc.name,
      resource: (svc.resources || [])[0] || "",
      date: new Date().toISOString().slice(0, 10),
      start_hour: 10, duration_hours: 2, price: 0, description: "",
    }]);
  };
  const updateFac = (idx, patch) => setFacilities((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeFac = (idx) => setFacilities((rows) => rows.filter((_, i) => i !== idx));
  const addAddon = () => setAddons((rows) => [...rows, { description: "", quantity: 1, unit_price: 0 }]);
  const updateAddon = (idx, patch) => setAddons((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeAddon = (idx) => setAddons((rows) => rows.filter((_, i) => i !== idx));

  const subtotal = facilities.reduce((s, r) => s + Number(r.price || 0), 0)
                 + addons.reduce((s, r) => s + Number(r.quantity || 0) * Number(r.unit_price || 0), 0);
  const gst = Math.round(subtotal * 0.18 * 100) / 100;
  const total = subtotal + gst;

  const save = async () => {
    if (!client.client_name.trim()) { toast.error("Client name required"); return; }
    if (facilities.length === 0 && addons.length === 0) { toast.error("Add at least one line item"); return; }
    setBusy(true);
    try {
      const body = {
        ...client,
        event_title: client.event_title || "Event Booking",
        facility_lines: facilities.map((f) => ({
          service_slug: f.service_slug, service_name: f.service_name, resource: f.resource,
          date: f.date, start_hour: parseInt(f.start_hour), duration_hours: parseInt(f.duration_hours),
          price: parseFloat(f.price) || 0, description: f.description,
        })),
        service_lines: addons.map((a) => ({
          description: a.description, quantity: parseFloat(a.quantity) || 1,
          unit_price: parseFloat(a.unit_price) || 0,
        })),
      };
      const { data } = await api.post("/admin/event-bills", body);
      toast.success(`Bill ${data.invoice_number} created · slots blocked on the matrix.`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-[#0B1120] border-2 border-brand-orange w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0B1120] z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// New Event Bill</div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">Create Event Bill</h2>
          </div>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Client / Event details */}
          <section>
            <div className="flex items-center gap-2 mb-3 text-brand-blue">
              <Building2 size={14}/>
              <div className="font-mono text-[11px] uppercase tracking-widest">Client details (for B2B GST invoice)</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Event title" value={client.event_title} onChange={(v) => setClient({ ...client, event_title: v })} placeholder="ABC Corp Cricket Day" tid="ev-title"/>
              <Input label="Event date (overall)" type="date" value={client.event_date} onChange={(v) => setClient({ ...client, event_date: v })} tid="ev-date"/>
              <Input label="Client / Company name *" value={client.client_name} onChange={(v) => setClient({ ...client, client_name: v })} placeholder="ABC Corporation Pvt Ltd" tid="ev-client-name"/>
              <Input label="Client GSTIN (optional)" value={client.client_gstin} onChange={(v) => setClient({ ...client, client_gstin: v.toUpperCase() })} placeholder="22AAAAA0000A1Z5" tid="ev-client-gstin"/>
              <Input label="Phone" value={client.client_phone} onChange={(v) => setClient({ ...client, client_phone: v })} placeholder="+91 98765 43210" tid="ev-client-phone"/>
              <Input label="Email" value={client.client_email} onChange={(v) => setClient({ ...client, client_email: v })} placeholder="accounts@abccorp.com" tid="ev-client-email"/>
              <div className="md:col-span-2">
                <Input label="Billing address" value={client.client_address} onChange={(v) => setClient({ ...client, client_address: v })} placeholder="Full billing address for the invoice" tid="ev-client-addr"/>
              </div>
              <div className="md:col-span-2">
                <Input label="Internal notes" value={client.notes} onChange={(v) => setClient({ ...client, notes: v })} placeholder="Any special arrangements, negotiated terms…" tid="ev-notes"/>
              </div>
            </div>
          </section>

          {/* Facility lines */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-brand-orange">
                <CalendarClock size={14}/>
                <div className="font-mono text-[11px] uppercase tracking-widest">Facility slots — will block the matrix</div>
              </div>
              <button onClick={addFacility} data-testid="ev-add-facility" className="text-[11px] font-mono uppercase tracking-widest brand-orange hover:underline flex items-center gap-1"><Plus size={11}/> Add facility</button>
            </div>
            {facilities.length === 0 && <div className="text-xs text-white/40 py-3 border border-dashed border-white/15 text-center">No facility lines yet.</div>}
            <div className="space-y-3">
              {facilities.map((f, idx) => {
                const svc = services.find((s) => s.slug === f.service_slug);
                const resources = svc?.resources || [];
                return (
                  <div key={idx} className="border border-white/12 bg-black/40 p-3" data-testid={`ev-fac-row-${idx}`}>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
                      <select value={f.service_slug} onChange={(e) => {
                          const nsvc = services.find((s) => s.slug === e.target.value);
                          updateFac(idx, { service_slug: e.target.value, service_name: nsvc?.name || "", resource: (nsvc?.resources || [])[0] || "" });
                        }}
                        data-testid={`ev-fac-svc-${idx}`}
                        className="col-span-2 bg-black/40 border border-white/15 px-2 py-2 text-xs">
                        {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                      </select>
                      <select value={f.resource} onChange={(e) => updateFac(idx, { resource: e.target.value })}
                        data-testid={`ev-fac-res-${idx}`}
                        className="bg-black/40 border border-white/15 px-2 py-2 text-xs">
                        {resources.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <input type="date" value={f.date} onChange={(e) => updateFac(idx, { date: e.target.value })}
                        data-testid={`ev-fac-date-${idx}`}
                        className="bg-black/40 border border-white/15 px-2 py-2 text-xs"/>
                      <select value={f.start_hour} onChange={(e) => updateFac(idx, { start_hour: parseInt(e.target.value) })}
                        data-testid={`ev-fac-start-${idx}`}
                        className="bg-black/40 border border-white/15 px-2 py-2 text-xs">
                        {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                      </select>
                      <input type="number" min="1" max="12" value={f.duration_hours}
                        onChange={(e) => updateFac(idx, { duration_hours: e.target.value })}
                        data-testid={`ev-fac-dur-${idx}`}
                        placeholder="hrs"
                        className="bg-black/40 border border-white/15 px-2 py-2 text-xs"/>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                      <input value={f.description} onChange={(e) => updateFac(idx, { description: e.target.value })}
                        placeholder="Optional description (shows on invoice)"
                        data-testid={`ev-fac-desc-${idx}`}
                        className="md:col-span-3 bg-black/40 border border-white/15 px-2 py-2 text-xs"/>
                      <div className="md:col-span-2 flex items-center gap-2">
                        <IndianRupee size={12} className="text-white/50"/>
                        <input type="number" min="0" step="100" value={f.price}
                          onChange={(e) => updateFac(idx, { price: e.target.value })}
                          placeholder="Flat price (pre-GST)"
                          data-testid={`ev-fac-price-${idx}`}
                          className="flex-1 bg-black/40 border border-white/15 px-2 py-2 text-xs font-mono"/>
                      </div>
                      <button onClick={() => removeFac(idx)} data-testid={`ev-fac-del-${idx}`} className="text-red-400 hover:text-red-300 justify-self-end"><Trash2 size={14}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Service / add-on lines */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-brand-blue">
                <Package size={14}/>
                <div className="font-mono text-[11px] uppercase tracking-widest">Add-on services & extras</div>
              </div>
              <button onClick={addAddon} data-testid="ev-add-service" className="text-[11px] font-mono uppercase tracking-widest brand-blue hover:underline flex items-center gap-1"><Plus size={11}/> Add service</button>
            </div>
            {addons.length === 0 && <div className="text-xs text-white/40 py-3 border border-dashed border-white/15 text-center">No add-on services yet (catering, staff, equipment rental, etc.).</div>}
            <div className="space-y-2">
              {addons.map((a, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-8 gap-2 items-center" data-testid={`ev-addon-row-${idx}`}>
                  <input value={a.description} onChange={(e) => updateAddon(idx, { description: e.target.value })}
                    placeholder="e.g. Catering — buffet for 40 guests"
                    data-testid={`ev-addon-desc-${idx}`}
                    className="md:col-span-4 bg-black/40 border border-white/15 px-2 py-2 text-xs"/>
                  <input type="number" min="0" step="0.5" value={a.quantity}
                    onChange={(e) => updateAddon(idx, { quantity: e.target.value })}
                    placeholder="Qty"
                    data-testid={`ev-addon-qty-${idx}`}
                    className="bg-black/40 border border-white/15 px-2 py-2 text-xs font-mono"/>
                  <div className="md:col-span-2 flex items-center gap-1">
                    <IndianRupee size={12} className="text-white/50"/>
                    <input type="number" min="0" step="10" value={a.unit_price}
                      onChange={(e) => updateAddon(idx, { unit_price: e.target.value })}
                      placeholder="Unit price"
                      data-testid={`ev-addon-price-${idx}`}
                      className="flex-1 bg-black/40 border border-white/15 px-2 py-2 text-xs font-mono"/>
                  </div>
                  <button onClick={() => removeAddon(idx)} data-testid={`ev-addon-del-${idx}`} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </section>

          {/* Totals */}
          <section className="border-t border-white/10 pt-4">
            <div className="max-w-sm ml-auto space-y-1 text-sm">
              <div className="flex justify-between text-white/70"><span>Subtotal</span><span className="font-mono">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between text-white/70"><span>GST @ 18%</span><span className="font-mono">{formatINR(gst)}</span></div>
              <div className="flex justify-between text-lg font-black brand-orange border-t border-white/20 pt-2 mt-1"><span>Total</span><span className="font-mono">{formatINR(total)}</span></div>
            </div>
          </section>
        </div>

        <div className="p-6 border-t border-white/10 flex gap-2 sticky bottom-0 bg-[#0B1120]">
          <button onClick={save} disabled={busy} data-testid="ev-save"
            className="flex-1 bg-brand-orange text-black hover:bg-white disabled:opacity-40 py-3 font-bold text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2">
            <Save size={14}/> {busy ? "Creating…" : "Create Bill & Block Slots"}
          </button>
          <button onClick={onClose} className="border border-white/20 hover:bg-white/10 px-8 py-3 font-bold text-xs uppercase tracking-widest">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ bill, onClose, onChanged }) {
  const [pay, setPay] = useState({ amount: bill.balance_due, method: "cash", txn_ref: "" });
  const [busy, setBusy] = useState(false);

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/event-bills/${bill.id}/mark-paid`, { amount: parseFloat(pay.amount), method: pay.method, txn_ref: pay.txn_ref });
      toast.success("Payment recorded"); onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this event bill and release all blocked slots? Cannot be undone.")) return;
    try { await api.delete(`/admin/event-bills/${bill.id}`); toast.success("Bill cancelled — slots released"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-[#0B1120] border-2 border-brand-orange w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// {bill.invoice_number}</div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">{bill.event_title}</h2>
            <div className="text-xs text-white/60 mt-1">Client: <b>{bill.client_name}</b>{bill.client_gstin && <> · GSTIN <span className="font-mono">{bill.client_gstin}</span></>}</div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/event-bills/${bill.id}/pdf`}
              target="_blank" rel="noopener noreferrer"
              data-testid="ev-download-pdf"
              className="bg-brand-orange text-black hover:bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5"
            >
              <Download size={12}/> Download PDF
            </a>
            <button onClick={onClose}><X size={20}/></button>
          </div>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Facility slots</div>
            {(bill.facility_lines || []).length === 0 && <div className="text-white/40 text-xs">—</div>}
            <div className="space-y-1">
              {(bill.facility_lines || []).map((f, i) => (
                <div key={i} className="flex justify-between border-b border-white/8 py-1.5">
                  <div>
                    <div>{f.service_name} · {f.resource} · {f.date} {String(f.start_hour).padStart(2,"0")}:00 → {String(f.start_hour + f.duration_hours).padStart(2,"0")}:00</div>
                    {f.description && <div className="text-white/50 text-xs">{f.description}</div>}
                  </div>
                  <div className="font-mono">{formatINR(f.price)}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Add-on services</div>
            {(bill.service_lines || []).length === 0 && <div className="text-white/40 text-xs">—</div>}
            <div className="space-y-1">
              {(bill.service_lines || []).map((a, i) => (
                <div key={i} className="flex justify-between border-b border-white/8 py-1.5">
                  <div>{a.description} <span className="text-white/50">× {a.quantity}</span></div>
                  <div className="font-mono">{formatINR(a.quantity * a.unit_price)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-white/15 pt-3 space-y-1">
            <div className="flex justify-between text-white/70"><span>Subtotal</span><span className="font-mono">{formatINR(bill.subtotal)}</span></div>
            <div className="flex justify-between text-white/70"><span>GST @ 18%</span><span className="font-mono">{formatINR(bill.gst_amount)}</span></div>
            <div className="flex justify-between text-lg font-black brand-orange"><span>Total</span><span className="font-mono">{formatINR(bill.total)}</span></div>
            <div className="flex justify-between text-green-300"><span>Paid</span><span className="font-mono">{formatINR(bill.amount_paid)}</span></div>
            <div className="flex justify-between text-yellow-300"><span>Balance</span><span className="font-mono">{formatINR(bill.balance_due)}</span></div>
          </div>

          {bill.status !== "paid" && bill.status !== "cancelled" && (
            <div className="border border-brand-blue/30 bg-brand-blue/5 p-3 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">Record payment</div>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="Amount"
                  data-testid="ev-pay-amt"
                  className="bg-black/40 border border-white/15 px-2 py-2 text-xs font-mono"/>
                <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}
                  data-testid="ev-pay-method"
                  className="bg-black/40 border border-white/15 px-2 py-2 text-xs">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                </select>
                <input value={pay.txn_ref} onChange={(e) => setPay({ ...pay, txn_ref: e.target.value })} placeholder="Txn ref (optional)"
                  data-testid="ev-pay-ref"
                  className="bg-black/40 border border-white/15 px-2 py-2 text-xs font-mono"/>
              </div>
              <button onClick={markPaid} disabled={busy} data-testid="ev-pay-save"
                className="w-full bg-brand-blue hover:bg-blue-500 text-white py-2 font-bold text-xs uppercase tracking-widest">
                {busy ? "Recording…" : "Record payment"}
              </button>
            </div>
          )}

          {bill.status !== "cancelled" && bill.amount_paid === 0 && (
            <button onClick={cancel} data-testid="ev-cancel"
              className="w-full border border-red-500/40 text-red-300 hover:bg-red-500/10 py-2 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2">
              <Ban size={12}/> Cancel bill & release blocked slots
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", tid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={tid}
        className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"/>
    </div>
  );
}
